import { useState, useEffect, useCallback } from 'react'
import { get, post, del } from '../lib/api'

export interface TalentNotionStatus {
  connected: boolean
  workspaceName?: string
  pageCount?: number
  connectedAt?: string
  lastSyncedAt?: string | null
}

export interface NotionSyncResult {
  status: string
  message?: string
}

export interface NotionPage {
  id: string
  title: string
  url: string
}

/** Structured Notion block — mirrors backend NotionBlockData. */
export interface NotionBlock {
  id: string
  type: string
  text: string
  hasChildren: boolean
}

/** Outcome of the most recent sync attempt. */
export type SyncStatus = null | 'success' | 'not_implemented'

export function useTalentNotion(talentId: string | null) {
  const [status, setStatus] = useState<TalentNotionStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(null)
  const [pages, setPages] = useState<NotionPage[]>([])
  const [pagesLoading, setPagesLoading] = useState(false)
  const [pagesError, setPagesError] = useState<string | null>(null)
  const [blocks, setBlocks] = useState<NotionBlock[]>([])
  const [blocksLoading, setBlocksLoading] = useState(false)
  const [blocksError, setBlocksError] = useState<string | null>(null)

  const basePath = talentId ? `/api/v1/talents/${talentId}/notion` : null

  const load = useCallback(async () => {
    if (!basePath) return
    setLoading(true)
    setError(null)
    try {
      const data = await get<TalentNotionStatus>(basePath)
      setStatus(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : '알 수 없는 오류')
    } finally {
      setLoading(false)
    }
  }, [basePath])

  useEffect(() => {
    if (!basePath) {
      setStatus(null)
      setSyncStatus(null)
      setPages([])
      setPagesError(null)
      return
    }
    setSyncStatus(null)
    load()
  }, [basePath, load])

  // POST /connect returns the full authoritative status — instant refresh.
  // We still reload to confirm server state.
  const connect = useCallback(
    async (token: string, workspaceName?: string) => {
      if (!basePath) return
      setError(null)
      try {
        const data = await post<TalentNotionStatus>(`${basePath}/connect`, {
          token,
          workspaceName: workspaceName || undefined,
        })
        setStatus(data)
      } catch (e) {
        setError(e instanceof Error ? e.message : '연결 실패')
      }
    },
    [basePath],
  )

  // DELETE returns 204 (no body) — reload to refresh authoritative state.
  const disconnect = useCallback(async () => {
    if (!basePath) return
    setError(null)
    setSyncStatus(null)
    try {
      await del<void>(basePath)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : '해제 실패')
    }
  }, [basePath, load])

  // POST /sync — Phase 2 stub returns { status: 'not_implemented' }.
  // Exposes the outcome so the UI can inform the user (no silent success).
  const sync = useCallback(async (): Promise<NotionSyncResult | null> => {
    if (!basePath) return null
    setError(null)
    setSyncing(true)
    try {
      const data = await post<NotionSyncResult>(`${basePath}/sync`)
      setSyncStatus(data?.status === 'not_implemented' ? 'not_implemented' : 'success')
      await load()
      return data
    } catch (e) {
      setError(e instanceof Error ? e.message : '동기화 실패')
      setSyncStatus(null)
      return null
    } finally {
      setSyncing(false)
    }
  }, [basePath, load])

  const loadPages = useCallback(async () => {
    if (!basePath) return
    setPagesLoading(true)
    setPagesError(null)
    try {
      const data = await get<NotionPage[]>(`${basePath}/pages`)
      setPages(data)
    } catch (e) {
      setPagesError(e instanceof Error ? e.message : '페이지 로드 실패')
    } finally {
      setPagesLoading(false)
    }
  }, [basePath])

  const loadBlocks = useCallback(async (pageId: string) => {
    if (!basePath) return
    setBlocksLoading(true)
    setBlocksError(null)
    try {
      const data = await get<NotionBlock[]>(`${basePath}/pages/${pageId}/blocks`)
      setBlocks(data)
    } catch (e) {
      setBlocksError(e instanceof Error ? e.message : '블록 로드 실패')
    } finally {
      setBlocksLoading(false)
    }
  }, [basePath])

  return {
    status,
    loading,
    error,
    syncing,
    syncStatus,
    connect,
    disconnect,
    sync,
    reload: load,
    pages,
    pagesLoading,
    pagesError,
    loadPages,
    blocks,
    blocksLoading,
    blocksError,
    loadBlocks,
  }
}
